import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {Component, Input, OnInit, OnDestroy} from '@angular/core';
import {CommonModule} from "@angular/common";
import {HttpClient} from "@angular/common/http";
import {TranslatePipe} from "@ngx-translate/core";
import {IconPathPipe} from "@app/utils/pipes/icon-path-pipe";

@Component({
  selector: 'main-status-tag',
  templateUrl: './main-status-tag.html',
  standalone: true,
  imports: [
    TranslatePipe,
    CommonModule,
    IconPathPipe
  ],
  styleUrl: './main-status-tag.scss'
})
export class MainStatusTag implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();


  @Input() iconSvgPath?: string;
  @Input() piIcon?: string;
  @Input() color: 'success' | 'info' | 'danger' | string = 'success';
  @Input() label: string = '';
  @Input() title?: string;
  @Input() description?: string;
  @Input() date?: string;
  @Input() time?: string;

  svgContent: string | null = null;

  constructor(private http: HttpClient) {}

  ngOnInit(): void {
    if (this.iconSvgPath) {
      this.loadSvgFromAssets(this.iconSvgPath);
    }
  }

  private loadSvgFromAssets(path: string): void {
    this.http.get(`assets/icons/main/light/${path}.svg`, { responseType: 'text' }).pipe(takeUntil(this.destroy$)).subscribe(
      (svg) => {
        this.svgContent = svg;
      },
      (error) => {
        console.error('Failed to load SVG:', error);
      }
    );
  }

  private colorSchemes = {
    success: {
      labelColor: '#16A34A',
      iconColor: '#16A34A'
    },
    info: {
      labelColor: '#3B82F6',
      iconColor: '#3B82F6'
    },
    danger: {
      labelColor: '#DC2626',
      iconColor: '#DC2626'
    }
  };

  getTagStyles() {
    return {};
  }

  getHeaderStyles() {
    const mainColor = this.getColorScheme().labelColor;
    return {
      'background-color': this.transparentize(mainColor, 0.1)
    };
  }

  getLabelColor(): string {
    return this.getColorScheme().labelColor;
  }

  getIconColor(): string {
    return this.getColorScheme().iconColor;
  }

  private getColorScheme() {
    if (this.isPresetColor()) {
      return this.colorSchemes[this.color as keyof typeof this.colorSchemes];
    }

    return {
      labelColor: this.color,
      iconColor: this.color
    };
  }

  private isPresetColor(): boolean {
    return ['success', 'info', 'danger'].includes(this.color);
  }

  private transparentize(color: string, alpha: number): string {
    if (!color || !color.startsWith('#')) {
      return 'rgba(0,0,0,0.05)';
    }

    let hex = color.substring(1);
    if (hex.length === 3) {
      hex = hex.split('').map(c => c + c).join('');
    }

    if (hex.length !== 6) {
      return 'rgba(0,0,0,0.05)';
    }

    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
